import React from 'react';
import createFlags from 'flag';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { takeLatest, put, select } from 'redux-saga/effects';

/**
 * Define default flags here, these may be overwritten by dispatching the setFlagsAction
 * and passing flags in the payload. Which we can use to load flags config from the API
 * when needed. Features can also be computed properties, just pass in a function instead
 * of a boolean, number or string.
 *
 * https://github.com/garbles/flag
 */
const defaultFlags = {
  features: {},
  isLead: false,
  tester: (flags) => !isEmpty(flags.features) && !flags.isLead,
  showFlagSettings: (flags) => flags.tester,
};

const { FlagsProvider, Flag, useFlag, useFlags } = createFlags({});

const MERGE_FLAGS_ACTION_TYPE = '@@FLAG/SET_FLAGS';

const setFlagsAction = (payload) => ({
  type: MERGE_FLAGS_ACTION_TYPE,
  payload,
});

const evaluateComputed = (flags) => {
  const compute = (value) => {
    if (typeof value === 'function') return value(flags);
    if (Array.isArray(value)) return value.map(compute);
    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc, key) => {
        acc[key] = compute(value[key]);
        return acc;
      }, {});
    }
    return value;
  };
  return compute(flags);
};

let prevFlags = null;
let prevComputed = null;

const getFlagsSelector = (state) => {
  if (prevFlags !== null && prevComputed !== null && prevFlags === state.flags) {
    return prevComputed;
  }
  prevFlags = state.flags;
  prevComputed = evaluateComputed(state.flags);
  return prevComputed;
};

const mergeFlags = (base, incoming) => {
  if (Array.isArray(base) || Array.isArray(incoming)) return incoming ?? base;
  if (base && typeof base === 'object' && incoming && typeof incoming === 'object') {
    const result = { ...base };
    Object.keys(incoming).forEach((key) => {
      result[key] = mergeFlags(base[key], incoming[key]);
    });
    return result;
  }
  return incoming !== undefined ? incoming : base;
};

const createFlagsReducer =
  (initialFlags) =>
  (state = initialFlags, action) => {
    if (
      action &&
      action.type === MERGE_FLAGS_ACTION_TYPE &&
      action.payload &&
      typeof action.payload === 'object'
    ) {
      return mergeFlags(state, action.payload);
    }
    return state;
  };

const ConnectedFlagsProvider = (props) => {
  const flags = useSelector((state) => state.flags);
  return React.createElement(FlagsProvider, { ...props, flags });
};

const reducer = createFlagsReducer(defaultFlags);

export const sagas = [
  function* resetFeatureFlags() {
    yield takeLatest('APP/FLUSH', function* () {
      // Make sure to flush all feature flags on app flush. We don't have a flush method
      // but we can go through all flags in the store and set the value to false, then
      // use that modifid flags list to set all to false
      const featureFlags = yield select((state) => state.flags.features);
      for (let flag in featureFlags) {
        featureFlags[flag] = false;
      }
      yield put(setFlagsAction({ features: featureFlags }));
    });
  },
];

export {
  reducer,
  Flag,
  useFlag,
  useFlags,
  setFlagsAction,
  getFlagsSelector,
  createFlagsReducer,
  ConnectedFlagsProvider,
};
